"use client";

import { ReactLenis, useLenis } from "lenis/react";
import { cancelFrame, frame } from "motion/react";
import React, { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import "lenis/dist/lenis.css";

export const useSmoothScroll = () => {
    const lenis = useLenis();
    return { lenis: lenis ?? null };
};

function LenisMotionSync() {
    const lenis = useLenis();
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!lenis) return;

        // 1. Sync Lenis animation frame with Framer Motion's frame update queue
        const update = (data: { timestamp: number }) => {
            lenis.raf(data.timestamp);
        };
        frame.update(update, true);

        // 2. Performance Safeguard: Add 'is-scrolling' class to body during active scroll
        const onScroll = () => {
            if (typeof document !== "undefined") {
                if (!document.body.classList.contains("is-scrolling")) {
                    document.body.classList.add("is-scrolling");
                }
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
                scrollTimeoutRef.current = setTimeout(() => {
                    document.body.classList.remove("is-scrolling");
                }, 150);
            }
        };

        lenis.on("scroll", onScroll);

        return () => {
            cancelFrame(update);
            lenis.off("scroll", onScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            if (typeof document !== "undefined") {
                document.body.classList.remove("is-scrolling");
            }
        };
    }, [lenis]);

    return null;
}

export function SmoothScrollProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const pathname = usePathname();
    const isLab = pathname?.startsWith("/lab");

    // Lab modules handle their own native scrolling & WebGL camera sync
    if (isLab) {
        return <>{children}</>;
    }

    return (
        <ReactLenis
            root
            options={{
                duration: 1.2,
                easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
                orientation: "vertical",
                gestureOrientation: "vertical",
                smoothWheel: true,
                wheelMultiplier: 1,
                touchMultiplier: 2,
                infinite: false,
                autoRaf: false, // Driven via motion/react frame.update
            }}
        >
            <LenisMotionSync />
            {children}
        </ReactLenis>
    );
}
